import React, { useMemo, useState } from 'react';
import {
  AlertBanner,
  CardDefault,
  Modal,
  SparkModalPortal,
  TagColored,
  Text,
  TooltipTrigger,
} from '@spark';
import { formatScore } from '@/shared/utils/metricFormatting';
import TeacherEvaluationFixMenu from './TeacherEvaluationFixMenu';
import './QualityMetrics.css';
import './QualityMetricsVersionComparison.css';

/** Human-readable duration for the teacher-run ETA, e.g. "2h 05m" / "7m". */
export const formatEtaSeconds = (etaSeconds) => {
  if (typeof etaSeconds !== 'number' || !Number.isFinite(etaSeconds) || etaSeconds <= 0) {
    return null;
  }
  const totalMinutes = Math.max(Math.round(etaSeconds / 60), 1);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  return `${minutes}m`;
};

/**
 * Render a backend warning string with the teacher model name as a code chip
 * (the backend quotes it, e.g. "'o3' is rate-limiting requests…").
 */
export const renderWarningWithModelChip = (warning, modelName) => {
  if (!warning || !modelName) return warning;
  const quoted = `'${modelName}'`;
  if (!warning.includes(quoted)) return warning;
  const parts = warning.split(quoted);
  return parts.flatMap((part, index) =>
    index === 0
      ? [part]
      : [
          <code key={`model-chip-${index}`} className="quality-metrics-model-chip">
            {modelName}
          </code>,
          part,
        ],
  );
};

const DEFAULT_METRIC_ORDER = [
  'accuracy',
  'precision',
  'recall',
  'f_beta_score',
  'f1_score',
];

/**
 * Plain-language explanation rendered under each metric name in the table.
 * Keep in sync with the Metric Optimisation dropdown copy in
 * features/trainings/components/finetuning/Hyperparams.jsx.
 */
export const METRIC_DESCRIPTIONS = {
  accuracy: 'How often was the model correct overall?',
  precision: 'When the model says ‘yes,’ how often is it right?',
  recall: 'Of all the items it should have found, how many did it find?',
  f_beta_score: 'Balance between precision and recall',
  f1_score: 'Balance between precision and recall',
};

export const getImprovementMeta = (improvement) => {
  if (typeof improvement !== 'string' || improvement.trim().length === 0) {
    return { kind: 'empty', text: '-' };
  }

  const trimmed = improvement.trim();
  // Defensive: a non-finite comparison (missing/failed baseline) is not a
  // real improvement value — render as "-".
  if (trimmed.includes('Infinity') || trimmed.includes('NaN')) {
    return { kind: 'empty', text: '-' };
  }
  if (trimmed === '0%' || trimmed === '+0.0%' || trimmed === '0.0%') {
    return { kind: 'neutral', text: '' };
  }

  if (trimmed.startsWith('-')) {
    return { kind: 'negative', text: trimmed };
  }

  return { kind: 'positive', text: trimmed };
};

const ImprovementPill = ({ improvement, disabled }) => {
  if (disabled) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }

  const meta = getImprovementMeta(improvement);
  if (meta.kind === 'empty' || meta.kind === 'neutral') {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }

  const variant = meta.kind === 'positive' ? 'lime' : 'volcano';
  const iconSlug = meta.kind === 'positive' ? 'trending-up' : 'trending-down';

  return (
    <span>
      <TagColored label={meta.text} variant={variant} showPrefixIcon prefixIconSlug={iconSlug} />
    </span>
  );
};

export const HeaderCell = ({ caption }) => (
  <div className="qmvc-header">
    {caption ? (
      <Text variant="uniq-semibold-table-caps" color="primary">{caption}</Text>
    ) : null}
  </div>
);

export const BenchmarkSubtitleCell = ({ benchmarkLabel, modelLabel }) => (
  <div className="qmvc-cell qmvc-cell--subtitle">
    <Text variant="base-regular" color="primary">{benchmarkLabel}</Text>
    <Text variant="detail-regular" color="tertiary">{modelLabel}</Text>
  </div>
);

export const EmptySubtitleCell = () => (
  <div className="qmvc-cell qmvc-cell--subtitle" aria-hidden="true" />
);

/** First-column cell: metric name with its plain-language explanation below. */
export const MetricNameCell = ({ metricKey, label }) => (
  <div className="qmvc-cell qmvc-cell--metric">
    <Text variant="base-regular" color="primary">{label}</Text>
    {METRIC_DESCRIPTIONS[metricKey] ? (
      <Text variant="detail-regular" color="tertiary">{METRIC_DESCRIPTIONS[metricKey]}</Text>
    ) : null}
  </div>
);

export const buildQualityMetricRows = (metrics) => {
  if (!metrics || typeof metrics !== 'object') return [];

  const seen = new Set();
  const rows = [];

  DEFAULT_METRIC_ORDER.forEach((key) => {
    const item = metrics?.[key];
    if (!item) return;
    rows.push({ key, ...item });
    seen.add(key);
  });

  Object.entries(metrics).forEach(([key, item]) => {
    if (seen.has(key) || !item) return;
    rows.push({ key, ...item });
  });

  return rows;
};

const ScoreCell = ({ value, isProcessing, isFailed, isCancelled, processingTooltip, teacherModelErrorDetails }) => {
  if (isCancelled) {
    return (
      <TooltipTrigger text="Teacher evaluation was cancelled. Re-run the evaluation to compute these metrics.">
        <span>
          <Text variant="base-regular" color="secondary">Cancelled</Text>
        </span>
      </TooltipTrigger>
    );
  }
  if (isProcessing) {
    const cell = <Text variant="base-regular" color="secondary">Processing…</Text>;
    if (!processingTooltip) return cell;
    return (
      <TooltipTrigger text={processingTooltip}>
        <span>{cell}</span>
      </TooltipTrigger>
    );
  }
  if (isFailed) {
    return (
      <TooltipTrigger text={teacherModelErrorDetails ? "Teacher evaluation failed. Click banner above for details." : "Teacher evaluation failed."}>
        <span>
          <Text variant="base-regular" style={{ color: 'var(--Semantic-Content-error, #dc2626)' }}>Failed</Text>
        </span>
      </TooltipTrigger>
    );
  }

  const formatted = formatScore(value, { decimals: 3 });
  if (formatted === null) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }

  const exact = formatScore(value, { decimals: 5 }) ?? formatted;

  return (
    <div>
      <TooltipTrigger text={`Exact value: ${exact}`}>
        <span>
          <Text variant="base-regular" color="primary">{formatted}</Text>
        </span>
      </TooltipTrigger>
    </div>
  );
};

const QualityMetrics = ({
  metrics,
  old_model,
  compareWith,
  currentVersion,
  currentVersionLabel,
  currentBenchmarkLabel,
  currentModelLabel,
  teacherModelProcessing,
  teacherModelFailed = false,
  teacherModelErrorDetails = null,
  studentModelProcessing,
  teacherEvaluationStatus = null,
  teacherModelCancelled = false,
  teacherModelWarning = null,
  teacherModelEtaSeconds = null,
  teacherModelQuotaLink = null,
  teacherModelAzureMode = null,
  teacherModelFailureDetails = null,
  onCancelTeacherEvaluation = null,
  singleModelView = false,
  metricsLoading = false,
}) => {
  const comparisonName = old_model || 'Comparison';
  const versionCaption = currentVersionLabel || (currentVersion ? `SpecificAI ${currentVersion}` : 'SpecificAI');
  const dataSource = useMemo(() => buildQualityMetricRows(metrics), [metrics]);
  const isTeacherComparison = compareWith === 'oldModel';
  const comparisonProcessing = teacherModelProcessing && isTeacherComparison;
  const comparisonFailed = teacherModelFailed && isTeacherComparison;
  const comparisonCancelled = teacherModelCancelled && isTeacherComparison && !comparisonProcessing;

  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelInFlight, setCancelInFlight] = useState(false);
  const [failureModalOpen, setFailureModalOpen] = useState(false);

  const teacherCounts = useMemo(() => {
    const completed = teacherEvaluationStatus?.completed;
    const total = teacherEvaluationStatus?.total;
    const failed = teacherEvaluationStatus?.failed;
    const hasCounts =
      typeof completed === 'number' &&
      Number.isFinite(completed) &&
      typeof total === 'number' &&
      Number.isFinite(total) &&
      total > 0;
    return {
      hasCounts,
      completed,
      total,
      failed: typeof failed === 'number' && Number.isFinite(failed) && failed > 0 ? failed : null,
    };
  }, [teacherEvaluationStatus]);

  const teacherTooltip = useMemo(() => {
    if (!comparisonProcessing) return null;
    if (!teacherCounts.hasCounts) return 'Teacher evaluation is running';
    const failedSuffix = teacherCounts.failed ? ` (${teacherCounts.failed} failed)` : '';
    return `Teacher evaluation: ${teacherCounts.completed}/${teacherCounts.total} records completed${failedSuffix}`;
  }, [comparisonProcessing, teacherCounts]);

  // Live warning/ETA from the websocket-updated status take precedence over
  // the page-load REST snapshot.
  const teacherWarning =
    (comparisonProcessing && teacherEvaluationStatus?.warning) || teacherModelWarning || null;
  const teacherEta = formatEtaSeconds(
    (comparisonProcessing && teacherEvaluationStatus?.eta_seconds) || teacherModelEtaSeconds,
  );
  const teacherQuotaLink =
    (comparisonProcessing && teacherEvaluationStatus?.quota_link) || teacherModelQuotaLink || null;
  const teacherAzureMode =
    (comparisonProcessing && teacherEvaluationStatus?.azure_mode) || teacherModelAzureMode || null;
  // Per-request failure details backing the "N failed" modal. While a run is
  // in progress, only the live status is authoritative — falling through to
  // the REST snapshot would show the *previous* run's errors next to the
  // current run's completed count. After the run ends, the version-scoped
  // REST snapshot is the source of truth.
  const teacherFailureDetails = useMemo(() => {
    const details = comparisonProcessing
      ? teacherEvaluationStatus?.failure_details
      : teacherModelFailureDetails;
    return Array.isArray(details) && details.length > 0 ? details : null;
  }, [comparisonProcessing, teacherEvaluationStatus, teacherModelFailureDetails]);

  const teacherBannerContent = useMemo(() => {
    if (comparisonCancelled) {
      return 'Teacher evaluation was cancelled. Re-run the evaluation to compute teacher quality metrics.';
    }
    if (comparisonProcessing) {
      // Compact single-line copy (Figma 5190:11482). The failed count is a
      // button that opens the failure-details modal; quota remediation lives
      // on the Fix menu rather than inline links.
      const headline = teacherCounts.hasCounts ? (
        <>
          {`Teacher evaluation in progress; ${teacherCounts.completed}/${teacherCounts.total} completed`}
          {teacherCounts.failed ? (
            <>
              {', '}
              <button
                type="button"
                className="quality-metrics-failed-link"
                onClick={() => setFailureModalOpen(true)}
              >
                {teacherCounts.failed} failed
              </button>
            </>
          ) : null}
        </>
      ) : (
        'Teacher evaluation in progress…'
      );
      const body = teacherWarning
        ? renderWarningWithModelChip(teacherWarning, old_model)
        : teacherEta && `Estimated time remaining: ~${teacherEta}.`;
      return (
        <span>
          {headline}
          {body ? (
            <>
              {teacherCounts.hasCounts ? '. ' : ' '}
              {body}
            </>
          ) : (
            teacherCounts.hasCounts ? '.' : null
          )}
        </span>
      );
    }
    // Terminal advisory (e.g. partial-failure summary) after the run ended.
    if (isTeacherComparison && teacherWarning) {
      if (!teacherFailureDetails) return teacherWarning;
      return (
        <span>
          <span>{teacherWarning}</span>
          {' '}
          <button
            type="button"
            className="quality-metrics-failed-link"
            onClick={() => setFailureModalOpen(true)}
          >
            View failure details
          </button>
        </span>
      );
    }
    return null;
  }, [
    comparisonCancelled,
    comparisonProcessing,
    isTeacherComparison,
    teacherCounts,
    teacherEta,
    teacherWarning,
    teacherFailureDetails,
    old_model,
  ]);

  // Boolean() matters: `undefined` would fall through to AlertBanner's
  // `showButton` default (true) and render a dangling cancel button on
  // cancelled/terminal banners.
  const showCancelButton = Boolean(comparisonProcessing && typeof onCancelTeacherEvaluation === 'function');
  const showQuotaActions = Boolean(comparisonProcessing && teacherWarning && teacherQuotaLink);
  const hasFixActions = showQuotaActions || showCancelButton;

  const handleConfirmCancel = async () => {
    if (cancelInFlight) return;
    setCancelInFlight(true);
    try {
      await onCancelTeacherEvaluation?.();
      setCancelConfirmOpen(false);
    } finally {
      setCancelInFlight(false);
    }
  };

  const currentSubtitle = currentBenchmarkLabel && currentModelLabel
    ? (
      <BenchmarkSubtitleCell
        benchmarkLabel={currentBenchmarkLabel}
        modelLabel={currentModelLabel}
      />
    )
    : <EmptySubtitleCell />;

  const comparisonSubtitle = currentBenchmarkLabel
    ? (
      <BenchmarkSubtitleCell
        benchmarkLabel={currentBenchmarkLabel}
        modelLabel={`${comparisonName} Model`}
      />
    )
    : <EmptySubtitleCell />;

  const metricNameCells = dataSource.map((row) => (
    <MetricNameCell metricKey={row.key} label={row.metric} key={`name-${row.key}`} />
  ));

  const currentScoreCells = dataSource.map((row) => (
    <div className="qmvc-cell" key={`new-${row.key}`}>
      <ScoreCell
        value={row.newModel}
        isProcessing={studentModelProcessing || metricsLoading}
        isFailed={false}
      />
    </div>
  ));

  const currentColumn = (
    <>
      <HeaderCell caption={versionCaption} />
      {currentSubtitle}
      {currentScoreCells}
    </>
  );

  return (
    <>
      {teacherBannerContent && (
        <div className="quality-metrics-teacher-banner">
          <AlertBanner
            feedback={comparisonCancelled ? 'info' : teacherWarning ? 'warning' : 'info'}
            showIcon
            text={teacherBannerContent}
            showButton={false}
            action={
              hasFixActions ? (
                <TeacherEvaluationFixMenu
                  quotaLink={showQuotaActions ? teacherQuotaLink : null}
                  azureMode={showQuotaActions ? teacherAzureMode : null}
                  onCancel={showCancelButton ? () => setCancelConfirmOpen(true) : undefined}
                />
              ) : undefined
            }
            showCloseButton={false}
          />
        </div>
      )}
    <CardDefault
      className="fs-unmask"
      type="collapsed"
      open
      fullWidth
      title="Quality Metrics"
      showSubtitle={false}
      border="yes"
    >
      <div className="quality-metrics-card-body">
        {dataSource.length === 0 ? (
          <Text variant="base-regular" color="secondary">No metrics available</Text>
        ) : (
          <div className="qmvc-body qmvc-body--metric-descriptions">
            <div className="qmvc-group">
              <div className="qmvc-col qmvc-col--names">
                <HeaderCell caption="" />
                <EmptySubtitleCell />
                {metricNameCells}
              </div>
              {!singleModelView && (
                <div className="qmvc-col">
                  <HeaderCell caption={comparisonName} />
                  {comparisonSubtitle}
                  {dataSource.map((row) => (
                    <div className="qmvc-cell" key={`old-${row.key}`}>
                      <ScoreCell
                        value={row.oldModel}
                        isProcessing={comparisonProcessing}
                        isFailed={comparisonFailed}
                        isCancelled={comparisonCancelled && row.oldModel == null}
                        processingTooltip={teacherTooltip}
                        teacherModelErrorDetails={teacherModelErrorDetails}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="qmvc-col">
                {currentColumn}
              </div>
              {!singleModelView && (
                <div className="qmvc-col">
                  <HeaderCell caption="Improvement" />
                  <EmptySubtitleCell />
                  {dataSource.map((row) => (
                    <div className="qmvc-cell" key={`imp-${row.key}`}>
                      <ImprovementPill
                        improvement={row.improvement}
                        disabled={
                          comparisonProcessing ||
                          row?.oldModel == null ||
                          (Number.isNaN(Number(row?.oldModel)) && !comparisonProcessing)
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {failureModalOpen ? (
        <SparkModalPortal
          open
          onClose={() => setFailureModalOpen(false)}
          size="text"
        >
          <Modal
            title={
              teacherCounts.failed
                ? `Teacher evaluation failures (${teacherCounts.failed})`
                : 'Teacher evaluation failures'
            }
            showSubtitle={false}
            feedbackType="warning"
            fixedHeight={false}
            showOverlay={false}
            showClose
            onCloseClick={() => setFailureModalOpen(false)}
            showFooter
            showTertiaryButton={false}
            primaryLabel="Close"
            onPrimaryClick={() => setFailureModalOpen(false)}
            bodyVariant="text"
          >
            <div className="quality-metrics-failure-details">
              {teacherFailureDetails ? (
                <>
                  {teacherFailureDetails.map((failure, index) => (
                    <div
                      className="quality-metrics-failure-details__item"
                      key={failure?.record_id || `failure-${index}`}
                    >
                      <Text variant="detail-regular" color="tertiary">
                        {failure?.record_id ? `Record ${failure.record_id}` : 'Batch-level error'}
                      </Text>
                      <Text variant="base-regular" color="primary">
                        {failure?.error || 'Unknown error'}
                      </Text>
                    </div>
                  ))}
                  {typeof teacherCounts.failed === 'number' &&
                    teacherCounts.failed > teacherFailureDetails.length && (
                      <Text variant="detail-regular" color="secondary">
                        Showing the first {teacherFailureDetails.length} of {teacherCounts.failed}{' '}
                        failed requests.
                      </Text>
                    )}
                </>
              ) : (
                <Text variant="base-regular" color="secondary">
                  Failure details are not available yet. They will appear here as failed requests
                  are confirmed, and the full list is available once the run completes.
                </Text>
              )}
            </div>
          </Modal>
        </SparkModalPortal>
      ) : null}
      {cancelConfirmOpen ? (
        <SparkModalPortal
          open
          onClose={() => setCancelConfirmOpen(false)}
          size="text"
        >
          <Modal
            title="Cancel teacher evaluation?"
            showSubtitle={false}
            feedbackType="warning"
            danger
            fixedHeight={false}
            showOverlay={false}
            showClose
            onCloseClick={() => setCancelConfirmOpen(false)}
            showFooter
            tertiaryLabel="Keep running"
            onTertiaryClick={() => setCancelConfirmOpen(false)}
            primaryLabel={cancelInFlight ? 'Cancelling…' : 'Cancel evaluation'}
            primaryDisabled={cancelInFlight}
            primaryDisableWhenReadOnly
            onPrimaryClick={handleConfirmCancel}
            bodyVariant="text"
          >
            <Text variant="base-regular" color="primary">
              The teacher evaluation will stop and its quality metrics will not be
              computed for this version. You can re-run the evaluation later.
            </Text>
          </Modal>
        </SparkModalPortal>
      ) : null}
    </CardDefault>
    </>
  );
};

export default QualityMetrics;
